/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { getSandboxImodels } from "services/SandboxIModels";
import { AttributeSerializer } from "services/share-service/serializers/AttributeSerializer";
import { sandboxUserLoaded } from "store/features";
import { CodeShare, CodeShareMetadata, CodeShareModule, OptionalFile, OptionalFileSet } from "types/codeshareTypes";
import { CodeShareUser } from "types/codeshareUser";
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./base-query";
import { codeShareMetadataListTransform, codeShareMetadataTransform, optionalFilesTransform, sourceFilesTransform } from "./transforms";
import { SandboxApi } from "./types";
import { runtimeEnv } from "configuration";
import { providesList } from "./helpers";

// Define a service using a base URL and expected endpoints
export const sandboxApi = createApi({
  reducerPath: "sandboxApi",
  tagTypes: ["codeshare", "codeshare-view", "thumbnail"],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    user: builder.query<CodeShareUser, void>({
      query: () => `user`,
    }),

    userCreate: builder.mutation<CodeShareUser, { username: string }>({
      query: (body) => ({
        url: `user`,
        method: "POST",
        body
      }),
      onQueryStarted: async (_args, api) => {
        const response = await api.queryFulfilled;
        api.dispatch(sandboxUserLoaded(response.data));
      }
    }),

    codeShares: builder.query<CodeShareMetadata[], { userId: string }>({
      query: ({ userId }) => `user/${userId}/codeshares`,
      providesTags: (result) => [...providesList(result, "codeshare"), { type: "codeshare", id: "OWNED-LIST" }],
      transformResponse: codeShareMetadataListTransform,
    }),

    codeSharesTemplates: builder.query<CodeShareMetadata[], void>({
      query: () => `codeshares/templates`,
      providesTags: (result) => providesList(result, "codeshare"),
      transformResponse: codeShareMetadataListTransform,
    }),

    codeSharesFeatured: builder.query<CodeShareMetadata[], void>({
      query: () => `codeshares/featured`,
      providesTags: (result) => providesList(result, "codeshare"),
      transformResponse: codeShareMetadataListTransform,
    }),

    codeSharesLiked: builder.query<CodeShareMetadata[], { userId: string }>({
      query: ({ userId }) => `user/${userId}/codeshares/liked`,
      providesTags: (result) => [...providesList(result, "codeshare"), { type: "codeshare", id: "LIKED-LIST" }],
      transformResponse: codeShareMetadataListTransform,
    }),

    codeShareLike: builder.mutation<CodeShareMetadata, SandboxApi.CodeShareLike>({
      query: ({ id, userId }) => ({
        url: `user/${userId}/codeshare/${id}/like`,
        method: "POST"
      }),
      invalidatesTags: (result) => result ? [{ type: "codeshare", id: result.id }, { type: "codeshare", id: "LIKED-LIST" }] : [],
      onQueryStarted: async (_args, api) => {
        const { data } = await api.queryFulfilled;
        api.dispatch(sandboxApi.util.updateQueryData("codeShareResolve", { userName: data.username, sandboxName: data.name }, (draft) => {
          draft.isLiked = data.isLiked;
        }));
        api.dispatch(sandboxApi.util.updateQueryData("codeShare", { userId: data.userId, codeshareId: data.id }, (draft) => {
          draft.isLiked = data.isLiked;
        }));
      },
    }),

    codeShare: builder.query<CodeShare, { userId: string, codeshareId: string }>({
      query: ({ userId, codeshareId }) => `user/${userId}/codeshare/${codeshareId}/view`,
      transformResponse: (response: SandboxApi.CodeShareDataResponse) => {
        const result: CodeShare = {
          ...codeShareMetadataTransform(response),
          files: sourceFilesTransform(response.files),
          allowedImodels: getSandboxImodels(response.iModels),
          attributes: AttributeSerializer.deserialize(response.attributes),
          optionalFiles: optionalFilesTransform(response.optionalFiles || []),
        };
        return result;
      },
      providesTags: (result) => result ? [{ type: "codeshare-view", id: result.id }] : []
    }),

    codeShareResolve: builder.query<CodeShare, { userName: string, sandboxName: string }>({
      queryFn: async ({ userName, sandboxName }, _api, _extraOptions, baseQuery) => {
        const response = await baseQuery(`username/${userName}/codeshare/${sandboxName}/view`);

        if (response.data) {
          const responseData = response.data as SandboxApi.CodeShareDataResponse;
          const data = {
            ...codeShareMetadataTransform(responseData),
            allowedImodels: getSandboxImodels(responseData.iModels),
            files: sourceFilesTransform(responseData.files),
            optionalFiles: optionalFilesTransform(responseData.optionalFiles || [])
          }

          return { data };
        }
        else
          return { error: response.error! };
      },
      providesTags: (result) => result ? [{ type: "codeshare-view", id: result.id }] : [],
      onQueryStarted: async (_args, api) => {
        const { data } = await api.queryFulfilled;
        await api.dispatch(sandboxApi.util.upsertQueryData("codeShare", { userId: data.userId, codeshareId: data.id }, data));
      },
    }),

    codeShareUpdate: builder.mutation<CodeShareMetadata, SandboxApi.CodeShareUpdate>({
      query: ({ id, userId, ...patch }) => ({
        url: `user/${userId}/codeshare/${id}`,
        method: "PATCH",
        body: patch,
      }),
      transformResponse: codeShareMetadataTransform,
      invalidatesTags: (result, _error) => {
        return result ? [{ type: "codeshare", id: "LIST" }] : []
      },
      onQueryStarted: async ({ id, userId, ...patch }, { dispatch, queryFulfilled }) => {
        try {
          const { data: updatedPost } = await queryFulfilled;
          dispatch(
            sandboxApi.util.updateQueryData("codeShare", { userId, codeshareId: id }, (draft) => {
              Object.assign(draft, updatedPost);
              if (patch.modules) draft.modules = patch.modules;
              if (patch.attributes) draft.attributes = patch.attributes;
              if (patch.iModels) draft.allowedImodels = getSandboxImodels(patch.iModels);
            })
          );
          dispatch(
            sandboxApi.util.updateQueryData("codeShareResolve", { userName: updatedPost.username, sandboxName: updatedPost.name }, (draft) => {
              Object.assign(draft, updatedPost);
              if (patch.modules) draft.modules = patch.modules;
              if (patch.attributes) draft.attributes = patch.attributes;
              if (patch.iModels) draft.allowedImodels = getSandboxImodels(patch.iModels);
            })
          );
        } catch { }
      },
    }),

    codeShareUpdateFiles: builder.mutation<CodeShareMetadata, SandboxApi.CodeShareUpdateFiles>({
      query: ({ id, userId, ...patch }) => ({
        url: `user/${userId}/codeshare/${id}`,
        method: "PATCH",
        body: patch,
      }),
      transformResponse: codeShareMetadataTransform,
      onQueryStarted: async ({ id, userId, ...patch }, { dispatch, queryFulfilled }) => {
        try {
          const { data: updatedPost } = await queryFulfilled;
          dispatch(
            sandboxApi.util.updateQueryData("codeShare", { userId, codeshareId: id }, (draft) => {
              if (patch.files) draft.files = sourceFilesTransform(patch.files);
            })
          );
          dispatch(
            sandboxApi.util.updateQueryData("codeShareResolve", { userName: updatedPost.username, sandboxName: updatedPost.name }, (draft) => {
              if (patch.files) draft.files = sourceFilesTransform(patch.files);
            })
          );
        } catch { }
      },
    }),

    codeShareCreate: builder.mutation<CodeShareMetadata, SandboxApi.CodeShareCreate>({
      query: (body) => ({
        url: `codeshare`,
        method: "POST",
        body,
      }),
      transformResponse: codeShareMetadataTransform,
      invalidatesTags: (result) => result ? [{ type: "codeshare", id: "OWNED-LIST" }] : [],
    }),

    codeShareDelete: builder.mutation<void, { userId: string, codeshareId: string }>({
      query: ({ userId, codeshareId }) => ({
        url: `user/${userId}/codeshare/${codeshareId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, args) => [{ type: "codeshare", id: args.codeshareId }],
    }),

    codeShareFork: builder.mutation<CodeShare, { forkUserId: string, forkCodeshareId: string, name: string, description?: string, attributes?: string[], modules?: CodeShareModule[], iModels?: string[] }>({
      query: (body) => ({
        url: `codeshares/fork`,
        method: "POST",
        body,
      }),
      transformResponse: (response: SandboxApi.CodeShareDataResponse) => {
        const result: CodeShare = {
          ...codeShareMetadataTransform(response),
          files: sourceFilesTransform(response.files),
          allowedImodels: getSandboxImodels(response.iModels),
          attributes: AttributeSerializer.deserialize(response.attributes),
          optionalFiles: optionalFilesTransform(response.optionalFiles || []),
        };
        return result;
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data: fork } = await queryFulfilled;
          await dispatch(
            sandboxApi.util.upsertQueryData("codeShare", { userId: fork.userId, codeshareId: fork.id }, fork)
          );
          await dispatch(
            sandboxApi.util.upsertQueryData("codeShareResolve", { userName: fork.username, sandboxName: fork.name }, fork)
          );
        } catch { }
      },
      invalidatesTags: (result) => result ? [{ type: "codeshare", id: "OWNED-LIST" }] : [],
    }),

    optionalFiles: builder.query<OptionalFileSet[], { filter?: OptionalFile[], includeBody?: boolean }>({
      query: ({ filter, includeBody }) => {
        const params: Record<string, any> = {};

        if (includeBody !== undefined)
          params.includeBody = includeBody;

        if (filter && filter.length)
          params.filter = filter.map((file) => `${file.name}@${file.version}`).join(";");

        return {
          url: "optionalFiles",
          params,
        };
      },
      transformResponse: (response: SandboxApi.OptionalFileSet[]) => {
        return response.map((fileSet) => {
          return {
            ...fileSet,
            files: sourceFilesTransform(fileSet.files, true)
          }
        });
      }
    }),

    thumbnail: builder.query<string, { userId: string, codeshareId: string, isAvailable?: boolean }>({
      keepUnusedDataFor: 604800, // cache for 7 days
      queryFn: async ({ userId, codeshareId, isAvailable }) => {
        if (!isAvailable)
          return { data: "" };

        try {
          const response = await fetch(`${runtimeEnv.REACT_APP_SANDBOX_BACKEND_URL}/api/user/${userId}/codeshare/${codeshareId}/thumbnail`);

          if (!response.ok)
            return { error: { status: response.status, data: "" } };

          const blob = await response.blob();
          return { data: URL.createObjectURL(new File([blob], `thumbnail-${userId}/${codeshareId}`)) };
        } catch (error) {
          return { error: { status: 500, data: error } };
        }
      },
      providesTags: (_result, _query, { codeshareId }) => {
        return [{ type: "thumbnail", id: codeshareId }]
      }
    }),

    thumbnailUpload: builder.mutation<void, { userId: string, codeshareId: string, thumbnail: File }>({
      query: ({ codeshareId, thumbnail }) => ({
        url: `codeshare/${codeshareId}/thumbnail`,
        method: "PUT",
        headers: {
          ContentType: "application/octet-stream",
          ContentDisposition: `form-data; name="file"; filename="${thumbnail.name}"`,
        },
        body: thumbnail,
      }),
      invalidatesTags: (_result, _query, { codeshareId }) => [{ type: "thumbnail", id: codeshareId }, { type: "codeshare", id: codeshareId }],
      onQueryStarted: async ({ userId, codeshareId, thumbnail }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          const data = URL.createObjectURL(new File([thumbnail], `thumbnail-${userId}/${codeshareId}`));
          await dispatch(
            sandboxApi.util.upsertQueryData("thumbnail", { userId, codeshareId }, data)
          );
        } catch { }
      },
    }),
    thumbnailDelete: builder.mutation<void, { codeshareId: string }>({
      query: ({ codeshareId }) => ({
        url: `codeshare/${codeshareId}/thumbnail`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _query, { codeshareId }) => [{ type: "thumbnail", id: codeshareId }, { type: "codeshare", id: codeshareId }]
    }),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useUserQuery,
  useCodeSharesQuery,
  useCodeShareQuery,
} = sandboxApi;
